import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import styles from './RightPane.module.scss';
import { List, Switch, Button } from '@mui/material';
import ListItemButton from '@mui/material/ListItemButton';
import Collapse from '@mui/material/Collapse';
import '../switch.scss';
import { useTranslation } from 'react-i18next';
import { PartUnion, MetadataUnion, GroupUnion } from '../../models/GroupUnion';
import GroupStateService from '../../services/GroupStateService';
import PartHighlightService from '../../services/PartHighlightService';
import { Part, Group, UnitDefinition, GetSystemResponse } from '../../models/Api';
import {
  getNextActionButtonForPart,
  deleteGroupConfirmDialog,
  deletePartConfirmDialog,
  convertUnit,
  getConvertedLabel,
  isPartDeletable,
  isGroupLastInLine,
  formatDecimals,
  confirmManifoldRevert,
  getDataPrecisionForDivision,
  manifoldStationDeleteAlert,
  genericPartEditingConfirmed,
} from '../../utils/Utils';
import SystemService from '../../services/SystemService';
import DialogBox from '../DialogBox/DialogBox';
import { isPartMountedOnManifold } from '../DashboardCanvas/ManifoldCanvasUtil';

type UnitConverterFunction = `unitSelector${string}UnitsConvert`;

declare global {
  interface Window {
    [key: UnitConverterFunction]: (unitBefore: number, valueBefore: number, unitCurrent: number) => number;
  }
}

export const RightPane = (props: {
  system: GetSystemResponse;
  divisionId: number;
  displayInMetric: boolean;
  units: UnitDefinition[];
  openSizingAlertDialog: () => void;
  isSizingTabOpen: () => boolean;
}) => {
  const { t } = useTranslation();
  const { system, displayInMetric, units, openSizingAlertDialog, isSizingTabOpen } = props;
  const decimalsToDisplay = getDataPrecisionForDivision(system.divisionId as number);
  const [groups, setGroups] = useState<GroupUnion[]>([] as GroupUnion[]);
  const [validateConfigureParts, setValidateConfigureParts] = useState({
    incompleteParts: '',
    showManifoldValidationAlert: false,
  });
  const [currentGroupAndPart, setCurrentGroupAndPart] = useState({
    groupUnion: {} as GroupUnion,
    part: {} as Part,
  });
  const [highlightedPart, setHighlightedPart] = useState('');
  const [manifoldActionName, setManifoldActionName] = useState('');
  const [showDeletePartDialog, setShowDeletePartDialog] = useState(false);
  const [deleteGroupDialog, setDeleteGroupDialog] = useState({
    show: false,
    withApplicationGroups: false,
  });
  const [isMetric, setIsMetric] = useState(displayInMetric);
  const [openItems, setOpenItems] = useState<string[]>([]);
  const [showPartDeleteAlertDialog, setShowPartDeleteAlertDialog] = useState(false);
  const [showGroupDeleteAlertDialog, setShowGroupDeleteAlertDialog] = useState(false);
  const [showOptimizedManifoldAlert, setShowOptimizedManifoldAlert] = useState(false);
  const [showDeleteManifoldValidation, setShowDeleteManifoldValidation] = useState(false);
  const [revertManifoldOptimize, setRevertManifoldOptimize] = useState(false);
  const [showConfirmGenericPartEditing, setShowConfirmGenericPartEditing] = useState(false);
  const manifoldGroupDefinitionId = props.system?.groupDefinitions?.find((definition) => definition.uniqueName === 'PneumaticSupplyManifolds')?.id;
  const manifoldGroupId = props.system?.groups?.find((group) => group.definitionId === manifoldGroupDefinitionId)?.id as number;
  const orderGroups = (groupUnions: GroupUnion[]) => {
    if (!groupUnions.length) return [];
    const groupsOnManifold: GroupUnion[] = [];
    const manifoldGroups: GroupUnion[] = [];
    const otherGroups: GroupUnion[] = [];
    props.system?.overlapGroupLinks?.map((overlapGroupObj) => {
      groupUnions.map((groupObj) => {
        if (groupObj.instance.id === overlapGroupObj.overlappingGroupId) {
          groupsOnManifold.push(groupObj);
        }
      });
    });
    const sortedManifoldGroups = groupsOnManifold.concat(groupUnions.filter((item) => !groupsOnManifold.includes(item)));
    sortedManifoldGroups.forEach((group) => {
      if (group.definition.uniqueName === 'PneumaticSupplyManifolds') {
        manifoldGroups.push(group);
      } else {
        otherGroups.push(group);
      }
    });
    return [...manifoldGroups, ...otherGroups];
  };

  function getParentsGroup(group: Group, parentGroups: string[]): string[] {
    if (group.parentGroupId === null) {
      parentGroups.push('g' + group.id);
      return parentGroups;
    }

    const parentGroup = groups.find((groupObj) => groupObj.instance.id === group.parentGroupId);
    parentGroups.push('g' + group.id);
    return getParentsGroup(parentGroup?.instance as Group, parentGroups);
  }

  useEffect(() => {
    if (highlightedPart !== '') {
      groups.find((group) =>
        group.parts.find((part) => {
          if (part.instance.id === highlightedPart) {
            setOpenItems([...getParentsGroup(group.instance, []), 'p' + part.instance.id]);
          }
        }),
      );
    } else {
      setOpenItems([]);
    }
  }, [highlightedPart]);

  useEffect(() => {
    PartHighlightService.getState(setHighlightedPart);
    GroupStateService.getState(setGroups);
  }, []);
  useEffect(() => {
    if (revertManifoldOptimize && manifoldActionName === 'delete') {
      deleteGroupConfirmDialog(
        currentGroupAndPart.groupUnion?.instance,
        deleteGroupDialog.show,
        setDeleteGroupDialog,
        deleteGroupDialog.withApplicationGroups,
        setShowOptimizedManifoldAlert,
        revertManifoldOptimize,
        setManifoldActionName,
        setShowDeleteManifoldValidation,
      );
    }
  }, [revertManifoldOptimize]);

  const handleGroupClick = (group: GroupUnion) => {
    setCurrentGroupAndPart({ groupUnion: group, part: {} });
    const groupId = 'g' + group.instance.id;
    const isParentGroup = group.instance.parentGroupId === null;
    if (openItems.includes(groupId)) {
      const index = openItems.indexOf(groupId);
      const items = index === 0 ? [] : openItems.slice(0, index);
      setOpenItems(items);
    } else {
      if (isParentGroup) {
        setOpenItems([groupId]);
      } else {
        setOpenItems((items) => [...items, groupId]);
      }
    }
  };

  const getPartId = (part: Part) => {
    return part.dataItems?.find((dataItem) => dataItem.key === 'PartId')?.value;
  };

  const handlePartClick = (part: Part) => {
    setCurrentGroupAndPart({ groupUnion: currentGroupAndPart.groupUnion, part: part });
    const partId = 'p' + part.id;
    if (openItems.includes(partId)) {
      setOpenItems((items) => items.filter((item) => item != partId));
    } else {
      setOpenItems((items) => [...items, partId]);
    }
  };

  const renderPart = (group: GroupUnion, level: number) => {
    return (
      <List component="div" disablePadding>
        {group.parts.map((part: PartUnion) => {
          const isPartOfManifold = part.category.name === 'PneumaticManifoldMasterPart' || isPartMountedOnManifold(part.instance);
          return (
            <div key={part.instance.id}>
              <ListItemButton sx={{ pl: level + 2 }} onClick={() => handlePartClick(part.instance as Part)}>
                <div className={styles['part-title']}>
                  {openItems.includes('p' + part.instance.id) ? (
                    <FontAwesomeIcon icon="caret-down" className={styles['caret-icon']} />
                  ) : (
                    <FontAwesomeIcon icon="caret-right" className={styles['caret-icon']} />
                  )}
                  {Object.keys(part.metadata)[0]}
                </div>
              </ListItemButton>

              <Collapse
                in={openItems.includes('p' + part.instance.id)}
                timeout="auto"
                unmountOnExit
                onClick={() => {
                  if (openItems.includes('p' + part.instance.id) && currentGroupAndPart.part.id !== part.instance.id) {
                    setCurrentGroupAndPart({
                      groupUnion: group,
                      part: part.instance,
                    });
                  }
                }}
              >
                {part.instance.isParkerPart && (
                  <div className={styles['right-pane-part-id']}>{getPartId(part.instance) ? getPartId(part.instance) : t('UnconfiguredPart')}</div>
                )}
                <div className={styles['right-pane-part-buttons']}>
                  {getNextActionButtonForPart(
                    part.instance,
                    isSizingTabOpen,
                    openSizingAlertDialog,
                    setValidateConfigureParts,
                    part,
                    setShowConfirmGenericPartEditing,
                  )}
                  {!isPartOfManifold && (
                    <Button
                      variant="outlined"
                      color="error"
                      className={styles['right-pane-delete-button']}
                      onClick={() => {
                        if (isPartDeletable(system, part.instance)) setShowDeletePartDialog(true);
                        else setShowPartDeleteAlertDialog(true);
                      }}
                    >
                      <FontAwesomeIcon icon="trash-can" />
                    </Button>
                  )}
                </div>
                <List component="div" disablePadding>
                  {part.metadata[Object.keys(part.metadata)[0]].map((partMetadata: MetadataUnion) => (
                    <ListItemButton key={partMetadata.instance.id} sx={{ pl: level + 3 }}>
                      <div className={styles['right-pane-units']}>
                        <div className={styles['metadata-name-div']}>{partMetadata.instance.name}</div>
                        <div className={styles['metadata-value-div']}>
                          <div className={styles['metadata-value']}>
                            {formatDecimals(
                              partMetadata.definition.dataType,
                              partMetadata.needsConversion ? convertUnit(units, partMetadata, isMetric).toString() : partMetadata.instance.value,
                              decimalsToDisplay,
                            )}
                          </div>

                          {partMetadata.unitDefinition?.unitLabel && (
                            <div
                              className={styles['metadata-unit']}
                              title={
                                partMetadata.needsConversion
                                  ? (getConvertedLabel(units, partMetadata, isMetric) as string)
                                  : (partMetadata.unitDefinition?.unitLabel as string)
                              }
                            >
                              {partMetadata.needsConversion ? getConvertedLabel(units, partMetadata, isMetric) : partMetadata.unitDefinition?.unitLabel}
                            </div>
                          )}
                        </div>
                      </div>
                    </ListItemButton>
                  ))}
                </List>
              </Collapse>
            </div>
          );
        })}
      </List>
    );
  };

  const renderGroup = (group: GroupUnion, level = 1) => {
    const isSegmentGroup = group.definition.uniqueName === 'PneumaticManifoldSegment';
    return (
      <div key={group.instance.id}>
        <ListItemButton key={group.instance.id} onClick={() => handleGroupClick(group as GroupUnion)} sx={{ pl: level + 1 }}>
          <div className={styles['group-title']}>
            {openItems.includes('g' + group.instance.id) ? (
              <FontAwesomeIcon icon="caret-down" className={styles['caret-icon']} />
            ) : (
              <FontAwesomeIcon icon="caret-right" className={styles['caret-icon']} />
            )}
            <span />
            {group.instance.name}
          </div>
        </ListItemButton>

        <Collapse
          in={openItems.includes('g' + group.instance.id)}
          timeout="auto"
          unmountOnExit
          onClick={() => {
            if (openItems.includes('g' + group.instance.id) && currentGroupAndPart.groupUnion?.instance?.id !== group.instance.id)
              setCurrentGroupAndPart({ groupUnion: group, part: {} });
          }}
        >
          <div className={styles['right-pane-group-buttons']}>
            {!isSegmentGroup && (
              <Button
                variant="outlined"
                color="error"
                className={styles['right-pane-delete-button']}
                onClick={() => {
                  const isLastGroup = isGroupLastInLine(system, group.instance.id as number);
                  if (isLastGroup) setDeleteGroupDialog({ show: true, withApplicationGroups: false });
                  else setShowGroupDeleteAlertDialog(true);
                }}
              >
                <FontAwesomeIcon icon="trash-can" />
              </Button>
            )}
          </div>
          <List component="div" disablePadding>
            {group.metadata.map((groupMetadata: MetadataUnion) => (
              <ListItemButton key={groupMetadata.instance.id} sx={{ pl: level + 2 }}>
                <div className={styles['right-pane-units']}>
                  <div className={styles['metadata-name-div']}>{groupMetadata.instance.name}</div>
                  <div className={styles['metadata-value-div']}>
                    <div className={styles['metadata-value']}>
                      {formatDecimals(
                        groupMetadata.definition.dataType,
                        groupMetadata.needsConversion ? convertUnit(units, groupMetadata, isMetric).toString() : groupMetadata.instance.value,
                        decimalsToDisplay,
                      )}
                    </div>
                    {groupMetadata?.unitDefinition?.unitLabel && (
                      <div
                        className={styles['metadata-unit']}
                        title={
                          groupMetadata.needsConversion
                            ? (getConvertedLabel(units, groupMetadata, isMetric) as string)
                            : (groupMetadata.unitDefinition?.unitLabel as string)
                        }
                      >
                        {groupMetadata.needsConversion ? getConvertedLabel(units, groupMetadata, isMetric) : groupMetadata.unitDefinition?.unitLabel}
                      </div>
                    )}
                  </div>
                </div>
              </ListItemButton>
            ))}
          </List>
        </Collapse>

        <Collapse in={openItems.includes('g' + group.instance.id)} timeout="auto" unmountOnExit>
          {groups.map((groupObj) => {
            if (groupObj.instance.parentGroupId === group.instance.id) {
              return renderGroup(groupObj, level + 1);
            }
          })}
        </Collapse>

        <Collapse in={openItems.includes('g' + group.instance.id)} timeout="auto" unmountOnExit>
          {renderPart(group, level)}
        </Collapse>
      </div>
    );
  };

  return (
    <>
      <DialogBox
        title={showPartDeleteAlertDialog ? t('CannotDeletePart') : t('CannotDeleteGroup')}
        message={showPartDeleteAlertDialog ? t('OnlyTheLastPart...') : t('OnlyTheLastPart...')}
        showModalState={showPartDeleteAlertDialog || showGroupDeleteAlertDialog}
        closeAction={() => {
          setShowPartDeleteAlertDialog(false);
          setShowGroupDeleteAlertDialog(false);
        }}
      />
      <DialogBox
        title={t('CannotSizeManifold')}
        message={t('PartsLinkedToValvesNotDone', { validateConfigureParts })}
        showModalState={validateConfigureParts.showManifoldValidationAlert}
        closeAction={() => {
          setValidateConfigureParts({
            incompleteParts: '',
            showManifoldValidationAlert: false,
          });
        }}
      />
      <DialogBox
        title={t('ConfirmPartResizingChanges', { partInstance: currentGroupAndPart.part })}
        message={t('ConfirmGenericPartEditingMessage')}
        showModalState={showConfirmGenericPartEditing}
        confirmOptions={{
          confirmTranslationKey: 'Yes',
          confirmAction: () => {
            genericPartEditingConfirmed(currentGroupAndPart.part);
          },
        }}
        closeAction={() => {
          setShowConfirmGenericPartEditing(false);
        }}
      />
      {currentGroupAndPart.groupUnion?.instance?.name &&
        !revertManifoldOptimize &&
        deleteGroupConfirmDialog(
          currentGroupAndPart.groupUnion?.instance,
          deleteGroupDialog.show,
          setDeleteGroupDialog,
          deleteGroupDialog.withApplicationGroups,
          setShowOptimizedManifoldAlert,
          revertManifoldOptimize,
          setManifoldActionName,
          setShowOptimizedManifoldAlert,
        )}
      {showOptimizedManifoldAlert &&
        confirmManifoldRevert(showOptimizedManifoldAlert, setShowOptimizedManifoldAlert, setRevertManifoldOptimize, manifoldGroupId)}
      {showDeleteManifoldValidation && manifoldStationDeleteAlert(showDeleteManifoldValidation, setShowDeleteManifoldValidation)}
      {currentGroupAndPart.part.name && deletePartConfirmDialog(currentGroupAndPart.part, showDeletePartDialog, setShowDeletePartDialog)}
      <div className={styles['right-pane']}>
        <div className={styles['right-pane-title-div']}>
          <p className={styles['title-text']}>{t('Units')} :</p>
          <div className={styles['switch-container']}>
            <span>{t('Metric')}</span>
            <Switch
              className="toggle-switch"
              checked={!isMetric}
              id="metricSwitch"
              onChange={async () => {
                setIsMetric(!isMetric);
                await SystemService.updateSystemUnits(!isMetric);
              }}
            />
            <span>{t('Imperial')}</span>
          </div>
        </div>
        <List sx={{ width: '100%' }} component="nav" aria-labelledby="nested-list-subheader">
          {orderGroups(groups).map((group) => {
            if (group.instance.parentGroupId === null) {
              return renderGroup(group);
            }
          })}
        </List>
      </div>
    </>
  );
};
